import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bundles, getKitsForBundle } from '../data/products';

const BundleCard = ({ bundle, featured = false, onBuy }) => {
  const includedKits = getKitsForBundle(bundle.id);

  return (
    <div className={`bundle-card ${featured ? 'featured' : ''}`}>
      {bundle.featured && <div className="featured-badge">Best Value</div>}
      <div className="bundle-header">
        <span className="bundle-icon">{bundle.icon}</span>
        <h3 className="bundle-name">{bundle.name}</h3>
      </div>
      <p className="bundle-description">{bundle.description}</p>

      <div className="bundle-stats">
        <div className="stat">
          <span className="stat-value">{bundle.kitCount}</span>
          <span className="stat-label">Kits</span>
        </div>
        <div className="stat">
          <span className="stat-value">{bundle.questionCount}</span>
          <span className="stat-label">Questions</span>
        </div>
        <div className="stat savings">
          <span className="stat-value">₹{bundle.savings}</span>
          <span className="stat-label">Savings</span>
        </div>
      </div>

      <div className="bundle-kits-preview">
        <span className="preview-label">Includes:</span>
        <div className="kit-icons">
          {includedKits.slice(0, 5).map(kit => (
            <span key={kit.id} className="kit-icon-small" title={kit.name}>
              {kit.icon}
            </span>
          ))}
          {includedKits.length > 5 && (
            <span className="kit-more">+{includedKits.length - 5}</span>
          )}
        </div>
      </div>

      <div className="bundle-pricing">
        <div className="price-display">
          <span className="original-price">₹{bundle.originalPrice}</span>
          <span className="current-price">
            <span className="currency">₹</span>
            {bundle.price}
          </span>
        </div>
      </div>

      <div className="bundle-actions">
        <Link to={`/bundles/${bundle.slug}`} className="btn btn-outline btn-full">
          View Details
        </Link>
        <button
          onClick={() => onBuy({ id: bundle.id, name: bundle.name, price: bundle.price })}
          className="btn btn-primary btn-full"
        >
          Get Bundle
        </button>
      </div>
    </div>
  );
};

const BundlesPage = () => {
  const { openCheckout } = useAuth();
  const completeBundle = bundles.find(b => b.id === 'complete-bundle');
  const otherBundles = bundles.filter(b => b.id !== 'complete-bundle');

  return (
    <div className="bundles-page">
      {/* Hero Section */}
      <section className="bundles-hero">
        <div className="container">
          <h1 className="bundles-title">Bundle Packages</h1>
          <p className="bundles-subtitle">
            Save big with our curated bundle packages. Perfect for focused interview preparation.
          </p>
        </div>
      </section>

      {/* Featured Complete Collection */}
      {completeBundle && (
        <section className="complete-bundle-section">
          <div className="container">
            <div className="complete-bundle-card">
              <div className="complete-badge">Best Value - Save ₹{completeBundle.savings}+</div>
              <div className="complete-content">
                <div className="complete-info">
                  <span className="complete-icon">{completeBundle.icon}</span>
                  <h2 className="complete-name">{completeBundle.name}</h2>
                  <p className="complete-description">{completeBundle.description}</p>

                  <div className="complete-stats">
                    <div className="complete-stat">
                      <span className="stat-number">{completeBundle.kitCount}</span>
                      <span className="stat-text">Interview Kits</span>
                    </div>
                    <div className="complete-stat">
                      <span className="stat-number">{completeBundle.questionCount}</span>
                      <span className="stat-text">Questions</span>
                    </div>
                    <div className="complete-stat">
                      <span className="stat-number">₹63</span>
                      <span className="stat-text">Per Kit</span>
                    </div>
                  </div>

                  <ul className="complete-highlights">
                    {completeBundle.highlights.map((h, i) => (
                      <li key={i}><span className="check">✓</span> {h}</li>
                    ))}
                  </ul>
                </div>

                <div className="complete-pricing">
                  <div className="pricing-box">
                    <span className="original">₹{completeBundle.originalPrice}</span>
                    <div className="current">
                      <span className="currency">₹</span>
                      <span className="amount">{completeBundle.price}</span>
                    </div>
                    <span className="savings-tag">Save ₹{completeBundle.savings}+</span>
                  </div>
                  <button
                    onClick={() => openCheckout({ id: completeBundle.id, name: completeBundle.name, price: completeBundle.price })}
                    className="btn btn-primary btn-large btn-full"
                  >
                    Get Complete Collection
                  </button>
                  <Link to={`/bundles/${completeBundle.slug}`} className="btn btn-outline btn-full complete-view-btn">
                    View All Included Kits
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Bundles Grid */}
      <section className="other-bundles-section">
        <div className="container">
          <h2 className="section-title center">Specialized Bundles</h2>
          <p className="section-subtitle">
            Choose a bundle based on your target role or technology stack
          </p>
          <div className="bundles-grid">
            {otherBundles.map(bundle => (
              <BundleCard key={bundle.id} bundle={bundle} onBuy={openCheckout} />
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="bundles-comparison-section">
        <div className="container">
          <h2 className="section-title center">Bundle Comparison</h2>
          <div className="bundles-comparison-table">
            <div className="comparison-header">
              <div className="comparison-cell header-cell">Bundle</div>
              <div className="comparison-cell header-cell">Kits</div>
              <div className="comparison-cell header-cell">Questions</div>
              <div className="comparison-cell header-cell">Price</div>
              <div className="comparison-cell header-cell">Savings</div>
            </div>
            {bundles.map(bundle => (
              <div key={bundle.id} className={`comparison-row ${bundle.featured ? 'featured-row' : ''}`}>
                <div className="comparison-cell">
                  <span className="bundle-row-icon">{bundle.icon}</span>
                  {bundle.name}
                </div>
                <div className="comparison-cell">{bundle.kitCount}</div>
                <div className="comparison-cell">{bundle.questionCount}</div>
                <div className="comparison-cell price-cell">₹{bundle.price}</div>
                <div className="comparison-cell savings-cell">₹{bundle.savings}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Kits CTA */}
      <section className="individual-kits-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Want to buy individual kits?</h2>
            <p>Browse our collection of 16 specialized interview kits</p>
            <Link to="/kits" className="btn btn-outline btn-large">
              Browse All Kits
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BundlesPage;
